import React, { useState, useEffect, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
import { Sidebar } from './components/Sidebar';
import { LogPane } from './components/LogPane';
import { AddAppModal } from './components/AddAppModal';
import { UserManagement } from './components/UserManagement';
import { Icons } from './components/Icons';
import { getApps, getConfiguredApps, checkAuth, requestMagicLink, logout, removeApp, getGlobalStats, AppInfo, AuthUser, GlobalStats, AUTH_ERROR_EVENT } from './services/api';

function App() {
  // Auth state
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  // App state
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);

  // Selected app (single view)
  const [selectedApp, setSelectedApp] = useState<string | null>(null);

  // Modals
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);

  // Global stats
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);

  // Check URL for auth errors
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorId = params.get('eid');
    if (error) {
      const errorMessages: Record<string, string> = {
        invalid_token: 'Invalid link. Please request a new one.',
        link_already_used: 'This link has already been used. Please request a new one.',
        link_expired: 'This link has expired. Please request a new one.',
        account_disabled: 'Your account has been disabled. Contact an administrator.',
        different_network: 'For security, you must open this link from the same network where you requested it. Please request a new link.',
        verification_failed: 'Authentication failed. Please try again.',
      };
      let message = errorMessages[error] || 'Authentication failed. Please try again.';
      if (errorId) {
        message += ` (Error ID: ${errorId})`;
      }
      setLoginError(message);
      // Clean URL
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Listen for auth errors from API calls and redirect to login
  useEffect(() => {
    const handleAuthError = () => {
      console.log('Auth error received, redirecting to login...');
      setUser(null);
      setSelectedApp(null);
      setLoginError('Your session has expired. Please log in again.');
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
  }, []);

  const checkAuthentication = async () => {
    setAuthLoading(true);
    try {
      const authUser = await checkAuth();
      setUser(authUser);
      if (authUser) {
        loadApps();
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const loadApps = async (showLoading = true) => {
    if (showLoading) setAppsLoading(true);
    try {
      // Phase 1: Show configured apps immediately (fast, no DB log query)
      if (showLoading) {
        try {
          const configured = await getConfiguredApps();
          applyAppOrder(configured);
          setApps(configured);
          setAppsLoading(false);
        } catch (err) {
          console.error('[loadApps] Phase 1 (getConfiguredApps) failed:', err);
        }
      }

      // Phase 2: Resolve hasData status for each app
      const loadedApps = await getApps();
      applyAppOrder(loadedApps);
      setApps(loadedApps);
    } catch (err) {
      console.error('[loadApps] Phase 2 (getApps) failed, apps stuck in pending state:', err);
      // Retry once after a short delay
      setTimeout(async () => {
        try {
          console.log('[loadApps] Retrying Phase 2...');
          const loadedApps = await getApps();
          applyAppOrder(loadedApps);
          setApps(loadedApps);
          console.log('[loadApps] Retry succeeded');
        } catch (retryErr) {
          console.error('[loadApps] Retry also failed:', retryErr);
        }
      }, 5000);
    } finally {
      if (showLoading) setAppsLoading(false);
    }
  };

  const loadGlobalStats = async () => {
    try {
      const stats = await getGlobalStats();
      setGlobalStats(stats);
    } catch (err) {
      console.error('Failed to load global stats:', err);
    }
  };

  // Load global stats periodically
  useEffect(() => {
    if (user) {
      loadGlobalStats();
      const interval = setInterval(loadGlobalStats, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  // Refresh apps list periodically to update connection status (silent refresh)
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => loadApps(false), 60000); // Every 60 seconds, no loading indicator
      return () => clearInterval(interval);
    }
  }, [user]);

  // Apply saved order to apps list
  const applyAppOrder = (appList: AppInfo[]) => {
    const savedOrder = localStorage.getItem('prepplogger_app_order');
    if (savedOrder) {
      try {
        const orderMap = JSON.parse(savedOrder) as Record<string, number>;
        appList.sort((a, b) => {
          const orderA = orderMap[a.name] ?? Number.MAX_SAFE_INTEGER;
          const orderB = orderMap[b.name] ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
      } catch {
        // Ignore invalid saved order
      }
    }
  };


  const handleReorderApps = useCallback((reorderedApps: AppInfo[]) => {
    setApps(reorderedApps);
    // Save order to localStorage
    const orderMap: Record<string, number> = {};
    reorderedApps.forEach((app, index) => {
      orderMap[app.name] = index;
    });
    localStorage.setItem('prepplogger_app_order', JSON.stringify(orderMap));
  }, []);

  const handleRemoveApp = useCallback(async (appName: string) => {
    try {
      await removeApp(appName);
      // Remove from local state
      setApps((prev) => prev.filter((app) => app.name !== appName));
      // Clear selection if this app was selected
      setSelectedApp((prev) => prev === appName ? null : prev);
      // Update order in localStorage
      const savedOrder = localStorage.getItem('prepplogger_app_order');
      if (savedOrder) {
        try {
          const orderMap = JSON.parse(savedOrder) as Record<string, number>;
          delete orderMap[appName];
          localStorage.setItem('prepplogger_app_order', JSON.stringify(orderMap));
        } catch {
          // Ignore
        }
      }
    } catch (err) {
      console.error('Failed to remove app:', err);
      alert(`Failed to remove app: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    setLoginMessage(null);

    try {
      const result = await requestMagicLink(loginEmail);
      setLoginMessage(result.message);
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Failed to send login link');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(null);
    setApps([]);
    setSelectedApp(null);
  };

  const handleSelectApp = useCallback((appName: string) => {
    setSelectedApp(appName);
  }, []);

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <Icons.Spinner className="w-8 h-8 mx-auto text-blue-400 mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
        <div
          className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}
        >
          {/* Header */}
          <div
            className="p-6 text-center"
            style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600/20 mb-4">
              <Icons.Server className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">PreppLogger</h1>
            <p className="text-gray-400 text-sm mt-1">Azure Web App Log Streaming</p>
          </div>

          {/* Form */}
          <div className="p-6">
            {loginMessage ? (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-600/20 mb-4">
                  <Icons.Mail className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="text-lg font-semibold text-white mb-2">Check your email</h2>
                <p className="text-gray-400 text-sm mb-6">
                  We've sent a sign-in link to <span className="text-white">{loginEmail}</span>
                </p>
                <button
                  onClick={() => {
                    setLoginMessage(null);
                    setLoginEmail('');
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleLogin}>
                {loginError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-600/20 border border-red-600/30">
                    <p className="text-red-400 text-sm">{loginError}</p>
                  </div>
                )}
                <div className="mb-6">
                  <label className="block text-sm text-gray-400 mb-2">Email address</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="input-modern"
                    autoFocus
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    We'll send you a magic link to sign in
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={!loginEmail || loginLoading}
                  className="w-full btn btn-primary py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loginLoading ? (
                    <>
                      <Icons.Spinner className="w-4 h-4" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icons.Mail className="w-4 h-4" />
                      Send Magic Link
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]"
        style={{ background: 'var(--bg-secondary)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center">
              <Icons.Server className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-lg font-semibold text-white">PreppLogger</span>
          </div>
          {selectedApp && (
            <span className="text-xs text-gray-500 px-2 py-1 rounded-full bg-[var(--bg-tertiary)]">
              {selectedApp}
            </span>
          )}
        </div>

        {/* Global Stats */}
        {globalStats && (
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5 text-gray-400">
              <Icons.Database className="w-3.5 h-3.5 text-purple-400" />
              <span>{formatBytes(globalStats.databaseSize)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400">
              <Icons.Layers className="w-3.5 h-3.5 text-blue-400" />
              <span>{globalStats.totalLogs.toLocaleString()} logs</span>
            </div>
            {globalStats.newestLog && (
              <div className="flex items-center gap-1.5 text-gray-400">
                <Icons.Clock className="w-3.5 h-3.5 text-green-400" />
                <span>Latest: {new Date(globalStats.newestLog).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{user.email}</span>
          {user.role === 'admin' && (
            <button
              onClick={() => setShowUserManagement(true)}
              className="btn btn-ghost text-sm"
              title="Manage users"
            >
              <Icons.Users className="w-4 h-4" />
              Users
            </button>
          )}
          <button onClick={handleLogout} className="btn btn-ghost text-sm">
            <Icons.LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          apps={apps}
          selectedApp={selectedApp}
          onSelectApp={handleSelectApp}
          onAddApp={user.role === 'admin' ? () => setShowAddAppModal(true) : undefined}
          onRemoveApp={user.role === 'admin' ? handleRemoveApp : undefined}
          onReorderApps={handleReorderApps}
          loading={appsLoading}
        />

        {/* Log View Area */}
        <div className="flex-1 p-4 overflow-hidden">
          {!selectedApp ? (
            /* Empty State */
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[var(--bg-tertiary)] mb-6">
                  <Icons.Layers className="w-10 h-10 text-gray-600" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Select an App</h2>
                <p className="text-gray-400 mb-6">
                  Choose an app from the sidebar to view and search its logs.
                </p>
                {apps.length > 0 ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    {apps.slice(0, 4).map((app) => (
                      <button
                        key={app.name}
                        onClick={() => handleSelectApp(app.name)}
                        className="btn btn-secondary text-sm"
                      >
                        <span
                          className={`status-dot ${
                            app.hasData === undefined
                              ? 'status-pending'
                              : app.hasData
                                ? 'status-has-data'
                                : 'status-no-data'
                          }`}
                        />
                        {app.name}
                      </button>
                    ))}
                  </div>
                ) : user.role === 'admin' ? (
                  <button
                    onClick={() => setShowAddAppModal(true)}
                    className="btn btn-primary"
                  >
                    <Icons.Plus className="w-4 h-4" />
                    Add Your First App
                  </button>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No apps configured. Contact an administrator to add apps.
                  </p>
                )}
              </div>
            </div>
          ) : (
            /* Log Pane */
            <div className="h-full">
              <LogPane
                key={selectedApp}
                appName={selectedApp}
                onClose={() => setSelectedApp(null)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add App Modal */}
      <AddAppModal
        isOpen={showAddAppModal}
        onClose={() => setShowAddAppModal(false)}
        onAppAdded={loadApps}
      />

      {/* User Management Modal (Admin only) */}
      {showUserManagement && (
        <UserManagement
          onClose={() => setShowUserManagement(false)}
        />
      )}
    </div>
  );
}

export default App;
